import { useState } from "react";

const BUTTONDOWN_API = "https://api.buttondown.email/v1/subscribers";
const BUTTONDOWN_API_KEY = import.meta.env.VITE_BUTTONDOWN_API_KEY;

/**
 * NewsletterSignup - Reusable newsletter signup component
 * 
 * Can be embedded on blog posts, product pages, etc.
 * 
 * @param {Object} props
 * @param {string} props.source - Source identifier for tracking (e.g., "blog_post", "product_page")
 * @param {string[]} props.tags - Additional tags to apply to subscriber
 * @param {string} props.variant - Display variant: "inline" (default) or "minimal"
 */
const NewsletterSignup = ({ 
  source = "newsletter_component", 
  tags = [],
  variant = "inline"
}) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setErrorMessage("Please enter your email address");
      setStatus("error");
      return;
    }

    setStatus("submitting");
    setErrorMessage("");

    try {
      const metadata = {
        source,
        timestamp: new Date().toISOString(),
      };

      const subscriberTags = ["exohaven", ...tags];

      const res = await fetch(BUTTONDOWN_API, {
        method: "POST",
        headers: {
          "Authorization": `Token ${BUTTONDOWN_API_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          metadata,
          tags: subscriberTags,
          referrer_url: window.location.href,
        }),
      });

      if (res.ok) {
        setStatus("success");
        setEmail(""); // Clear input
      } else {
        const data = await res.json();
        
        if (res.status === 400 && data.email) {
          setErrorMessage("This email is already subscribed!");
        } else {
          setErrorMessage(data.detail || data.error || "Something went wrong. Please try again.");
        }
        setStatus("error");
      }
    } catch (err) {
      console.error("Newsletter signup error:", err);
      setErrorMessage("Unable to connect. Please try again later.");
      setStatus("error");
    }
  };

  // Minimal variant - just email field and button in a row
  if (variant === "minimal") {
    return (
      <div className="w-full max-w-md">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            disabled={status === "success"}
            className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-2
                       text-gray-900 placeholder-gray-400 focus:outline-none
                       focus:border-pink-500 transition-colors disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={status === "submitting" || status === "success"}
            className="bg-pink-600 hover:bg-pink-700 disabled:bg-gray-700
                       disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg
                       font-medium transition-colors whitespace-nowrap">
            {status === "submitting" ? "..." : status === "success" ? "✓" : "Subscribe"}
          </button>
        </form>
        
        {status === "success" && (
          <p className="text-green-400 text-sm mt-2">
            Thanks! Check your inbox for confirmation.
          </p>
        )}
        
        {status === "error" && (
          <p className="text-red-400 text-sm mt-2">{errorMessage}</p>
        )}
      </div>
    );
  }

  // Inline variant - full signup box with heading
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
      <h3 className="text-xl font-semibold text-white mb-2">
        Join the ExoHaven Newsletter
      </h3>
      <p className="text-gray-400 text-sm mb-4">
        Weekly insights on focused work and narrative control. No spam.
      </p>

      {status === "success" ? (
        <div className="bg-green-900/30 border border-green-800 text-green-300 px-4 py-3 rounded-lg">
          <p className="font-medium">You're subscribed! 🎉</p>
          <p className="text-sm mt-1">Check your inbox for a confirmation email.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3
                       text-gray-900 placeholder-gray-400 focus:outline-none
                       focus:border-pink-500 transition-colors"
          />

          {status === "error" && (
            <div className="bg-red-900/50 border border-red-800 text-red-300 px-3 py-2 rounded-lg text-sm">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={status === "submitting"}
            className="w-full bg-pink-600 hover:bg-pink-700 disabled:bg-gray-700
                       disabled:cursor-not-allowed text-white py-3 rounded-lg
                       font-semibold transition-colors">
            {status === "submitting" ? "Subscribing..." : "Subscribe"}
          </button>
        </form>
      )}
    </div>
  );
};

export default NewsletterSignup;
